import { x25519 } from '@noble/curves/ed25519.js';
import type { RegisterDevicePayload } from '@shared/types';
import { authAPI } from '../api/client';
import { signDeviceKeyBindingWithKey } from '../crypto/deviceKeyBinding';
import { createPublicKey } from '../crypto/keys';
import { signRequestWithKey } from '../crypto/signing';
import { useAuthStore } from '../store/authStore';

let started = false;

function deriveEncryptionPublicKey(deviceId: string): { algorithm: 'x25519'; value: string } | null {
  const device = useAuthStore.getState().devices[deviceId];
  if (!device) return null;
  if (device.encryptionPublicKey?.algorithm === 'x25519' && device.encryptionPublicKey.value) {
    return {
      algorithm: 'x25519',
      value: device.encryptionPublicKey.value
    };
  }
  try {
    const publicKey = createPublicKey(x25519.getPublicKey(device.ecdhScalar), 'x25519');
    return {
      algorithm: 'x25519',
      value: publicKey.value
    };
  } catch {
    return null;
  }
}

async function healDevice(deviceId: string): Promise<void> {
  const state = useAuthStore.getState();
  const device = state.devices[deviceId];
  if (!device) return;
  const identity = state.identities[device.identityId];
  if (!identity?.serverUrl) return;

  const deviceEncryptionPublicKey = deriveEncryptionPublicKey(deviceId);
  if (!deviceEncryptionPublicKey) return;

  const payload: RegisterDevicePayload = {
    deviceId: device.deviceId,
    devicePublicKey: device.publicKey,
    deviceEncryptionPublicKey,
    deviceKeyBinding: await signDeviceKeyBindingWithKey({
      identityId: identity.identityId,
      devicePublicKey: device.publicKey,
      deviceEncryptionPublicKey,
      deviceSigningKey: device.signingKey
    }),
    deviceLabel: device.label
  };

  const signedDeviceRequest = await signRequestWithKey(
    'auth:register-device',
    payload,
    identity.identityId,
    identity.signingKey
  );

  await authAPI.registerDevice(signedDeviceRequest, identity.serverUrl);
}

export async function startDeviceRegistrationSelfHeal(): Promise<void> {
  if (started) return;
  started = true;

  const state = useAuthStore.getState();
  const deviceIds = Object.keys(state.devices);
  await Promise.allSettled(deviceIds.map((deviceId) => healDevice(deviceId)));
}
